import { useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useT } from '../../../messages';

/**
 * A panel that slides over the inspector's side — "one click deeper" for the
 * controls the builder keeps out of the way: every token, the pages and the
 * bar, the stylesheet. Modal, with `SiteDialog`'s manners: Esc closes, Tab is
 * trapped, focus starts inside and goes back to what opened it.
 *
 * Modal on purpose, although the canvas stays visible beside it: a colour
 * changed here repaints the canvas at once, and that is the whole point of
 * seeing it — but a click on the canvas while a drawer is open would change
 * the selection under a panel that is not about the selection.
 */
export function Drawer({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}): ReactNode {
  const t = useT();
  const panel = useRef<HTMLDivElement>(null);
  const latest = useRef(onClose);
  latest.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    focusables(panel.current)[0]?.focus();
    return () => {
      if (opener !== null && opener.isConnected) opener.focus();
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      latest.current();
      return;
    }
    if (e.key !== 'Tab') return;
    const all = focusables(panel.current);
    const first = all[0];
    const last = all[all.length - 1];
    if (first === undefined || last === undefined) {
      e.preventDefault();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="bld-drawer-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={panel}
        className="bld-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={onKeyDown}
      >
        <div className="bld-drawer__head">
          <h3>{title}</h3>
          <button
            type="button"
            className="bld-iconbtn"
            aria-label={t('builder.close')}
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="bld-drawer__body">{children}</div>
      </div>
    </div>
  );
}

export function focusables(root: HTMLElement | null): HTMLElement[] {
  if (root === null) return [];
  return [
    ...root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
}
